import React from 'react';
import Icon from '../../../components/AppIcon';

const EngagementMetrics = ({ metrics }) => {
  const getSentimentColor = (label) => {
    switch (label) {
      case 'positive': return 'text-success';
      case 'neutral': return 'text-muted-foreground';
      case 'negative': return 'text-error';
      case 'mixed': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getSentimentIcon = (label) => {
    switch (label) {
      case 'positive': return 'Smile';
      case 'neutral': return 'Meh';
      case 'negative': return 'Frown';
      case 'mixed': return 'TrendingUp';
      default: return 'Activity';
    }
  };

  const getQualityColor = (quality) => {
    switch (quality) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-success';
      case 'fair': return 'text-warning';
      case 'poor': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins)?.padStart(2, '0')}:${String(secs)?.padStart(2, '0')}`;
  };

  if (!metrics) {
    return (
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Activity" size={20} className="text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Engagement Metrics</h3>
        </div>
        <p className="text-sm text-muted-foreground">Loading metrics...</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Icon name="Activity" size={20} className="text-primary" />
        <h3 className="text-sm font-semibold text-foreground">Engagement Metrics</h3>
      </div>

      {/* Sentiment Tracking */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon 
              name={getSentimentIcon(metrics?.sentimentLabel)} 
              size={16} 
              className={getSentimentColor(metrics?.sentimentLabel)} 
            />
            <span className="text-sm font-medium text-foreground">Sentiment</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-semibold ${getSentimentColor(metrics?.sentimentLabel)}`}>
              {metrics?.sentimentScore?.toFixed(1) || 0}%
            </span>
            <span className="text-xs text-muted-foreground capitalize">
              ({metrics?.sentimentLabel || 'neutral'})
            </span>
          </div>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${
              metrics?.sentimentLabel === 'positive' ? 'bg-success' :
              metrics?.sentimentLabel === 'negative' ? 'bg-error' :
              metrics?.sentimentLabel === 'mixed'? 'bg-warning' : 'bg-muted-foreground'
            }`}
            style={{ width: `${metrics?.sentimentScore || 0}%` }}
          />
        </div>
      </div>

      {/* Session Duration */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="Clock" size={16} className="text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Duration</span>
        </div>
        <span className="text-sm font-mono font-semibold text-foreground">
          {formatDuration(metrics?.sessionDuration || 0)}
        </span>
      </div>

      {/* Connection Quality */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="Wifi" size={16} className={getQualityColor(metrics?.connectionQuality)} />
            <span className="text-sm font-medium text-foreground">Connection</span>
          </div>
          <span className={`text-sm font-semibold capitalize ${getQualityColor(metrics?.connectionQuality)}`}>
            {metrics?.connectionQuality || 'unknown'}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center justify-between bg-muted/50 rounded px-2 py-1">
            <span className="text-muted-foreground">Latency</span>
            <span className="font-medium text-foreground">
              {metrics?.connectionLatency?.toFixed(0) || 0}ms
            </span>
          </div>
          <div className="flex items-center justify-between bg-muted/50 rounded px-2 py-1">
            <span className="text-muted-foreground">Loss</span>
            <span className="font-medium text-foreground">
              {metrics?.packetLossRate?.toFixed(1) || 0}%
            </span>
          </div>
        </div>
      </div>

      {/* Engagement Indicators */}
      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
        <div className="flex items-center gap-2">
          <Icon name="MessageSquare" size={14} className="text-primary" />
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">Messages</span>
            <span className="text-sm font-semibold text-foreground">
              {metrics?.messageCount || 0}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="MousePointer" size={14} className="text-primary" />
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">Interactions</span>
            <span className="text-sm font-semibold text-foreground">
              {metrics?.interactionCount || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Video Quality */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <div className="flex items-center gap-2">
          <Icon name="Video" size={14} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Video Quality</span>
        </div>
        <span className="text-xs font-medium text-foreground uppercase">
          {metrics?.videoQuality || 'auto'}
        </span>
      </div>
    </div>
  );
};

export default EngagementMetrics;